import { Link } from '@remix-run/react';
import cls from 'classnames';
import PlayFillIcon from 'icon/play_fill.svg';
import HeartIcon from 'icon/heart.svg';
import { FC, useCallback, useMemo } from 'react';
import { useNumFormatter } from '~/utils/format';
import { useAtomValue } from 'jotai';
import { useViewTracking } from '~/utils/use-view-tracking';
import { ModuleType } from '~/utils/module-type';
import { ClickPosition, ClickPositionPrefix } from '~/utils/click-position';
import useReflowClick from '~/utils/use-reflow-click';
import { Article, LegacyArticleUtil } from 'shared/models/legacy';
import { useActiveToArticleBySimpleArticle } from '~/utils/active/use-active-to-article';
import { $IsMobileUser } from '~/utils/is-mobile-user';
import { useSEOHref } from '~/utils/build-seo-url';

import './style.scss';

interface EndRecommendCardProps {
  article: Article;
  size: 'normal' | 'small';
  rank: number;
  positionPrefix?: ClickPositionPrefix;
}

export const EndRecommendCard: FC<EndRecommendCardProps> = (props) => {
  const { article, size, rank, positionPrefix = ClickPositionPrefix.none } = props;
  const activeToArticle = useActiveToArticleBySimpleArticle(article);
  const isMobileUser = useAtomValue($IsMobileUser);

  const {
    author: { nickName, avatar: authorAvatar, userId },
    title,
    articleClass,
    groupId,
    articleLikes,
    largeImage,
    imageList,
  } = article;

  const formatNum = useNumFormatter();

  const cardObservedRef = useViewTracking<HTMLAnchorElement>({
    onView: useCallback(() => {
      if (isMobileUser) {
        window.Tea('wap_cta_show', {
          current_page_module: positionPrefix + ModuleType.main_related_post,
          current_position: positionPrefix + ClickPosition.main_related_post,
          group_id: groupId,
          media_id: userId,
          article_class: articleClass,
          rank,
        });
      }
      window.Tea('wap_related_post_show', {
        current_page_module: positionPrefix + ModuleType.main_related_post,
        current_position: positionPrefix + ClickPosition.main_related_post,
        group_id: groupId,
        media_id: userId,
        impr_id: article.imprId,
        article_class: articleClass,
        rank,
      });
    }, [article, rank, groupId, userId, articleClass]),
  });

  const handleEventClick = useReflowClick(
    {
      clickEvent: () => ({
        current_position: positionPrefix + ClickPosition.main_related_post,
        current_page_module: positionPrefix + ModuleType.main_related_post,
        page_click_id: groupId,
        rank,
      }),
      reflowBehavior: () => {
        activeToArticle([positionPrefix, ClickPosition.main_related_post], groupId);
      },
    },
    [groupId],
  );

  const articleLink = useSEOHref(LegacyArticleUtil.getArticlePath(article));

  const authorLink = useSEOHref(LegacyArticleUtil.getAuthorPath(article));

  const imageSrc = useMemo(() => {
    if (articleClass === 'Gallery') {
      return imageList?.[0]?.url || '';
    }

    return largeImage.url;
  }, [articleClass, imageList, largeImage]);

  return (
    <object className={cls('seo-recommend-card', size)}>
      <Link to={articleLink} ref={cardObservedRef} onClick={handleEventClick}>
        <img className="seo-recommend-card-img" src={imageSrc} alt={title} />
        <div className="seo-recommend-card-mask" />
        {articleClass === 'Video' && <PlayFillIcon className="seo-recommend-card-play" />}
        <div className="seo-recommend-card-main">
          <p className="seo-recommend-card-main-title">{title}</p>
          <object>
            <Link to={authorLink} className="seo-recommend-card-main-desc" onClick={handleEventClick}>
              <img className="avatar" src={authorAvatar} alt={nickName} />
              <span className="user-name">{nickName}</span>
              {Boolean(articleLikes) && (
                <div className="digg">
                  <HeartIcon />
                  {formatNum(Number(articleLikes))}
                </div>
              )}
            </Link>
          </object>
        </div>
      </Link>
    </object>
  );
};
